/**
 * The global brake, rendered loudly (#2575).
 *
 * The settings row ships CLOSED, so "off" is the ordinary state of a
 * freshly promoted environment. A grey pill would be that failure mode
 * wearing a UI: everyone assumes mail is going out and none is. A failed
 * read must NOT fall through to a rendered "off".
 */
import { useEffect, useState } from 'react';
import type { CodexCapacityAlertSettings } from '@nadoutong/contracts';
import { useOpsUserLabel } from '../../api/directory.ts';
import { formatLocalTimestamp } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, EmptyState, FormField, Input, Panel, Skeleton, Switch } from '../../ui/index.ts';
import { CodexCapacitySwitchDialog } from './CodexCapacitySwitchDialog.tsx';
import { readSwitchState, switchStateLabel, switchStateTone } from './codexCapacityStatus.ts';

interface Props {
  readonly settings: CodexCapacityAlertSettings | null;
  readonly loading: boolean;
  readonly error: boolean;
  readonly saving: boolean;
  readonly onRetry: () => void;
  readonly onSubmit: (next: { readonly enabled: boolean; readonly reason: string | null }) => void;
}

export function CodexCapacitySwitchPanel({
  settings,
  loading,
  error,
  saving,
  onRetry,
  onSubmit,
}: Props): JSX.Element {
  const t = useT();
  const [pending, setPending] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (pending === null) setReason('');
  }, [pending]);

  if (loading) {
    return (
      <Panel caption={t('codex_capacity.switch.caption')} padded testId="codex-capacity-switch-loading">
        <Skeleton className="h-20 w-full" />
      </Panel>
    );
  }

  if (error || settings === null) {
    return (
      <Panel caption={t('codex_capacity.switch.caption')} padded testId="codex-capacity-switch-error">
        <EmptyState
          title={t('codex_capacity.switch.error')}
          hint={t('codex_capacity.switch.error_hint')}
          action={
            <Button variant="secondary" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          }
        />
      </Panel>
    );
  }

  const state = readSwitchState(settings);

  return (
    <Panel
      caption={t('codex_capacity.switch.caption')}
      padded
      testId="codex-capacity-switch-panel"
      actions={
        <Chip tone={switchStateTone(state)} testId="codex-capacity-switch-chip">
          {t(switchStateLabel(state))}
        </Chip>
      }
    >
      <div className="flex flex-col gap-3">
        <StateBanner state={state} settings={settings} />
        <div className="flex flex-wrap items-center gap-3">
          <Switch
            checked={settings.enabled}
            disabled={saving}
            onCheckedChange={(next) => setPending(next)}
            label={t('codex_capacity.switch.toggle_label')}
            testId="codex-capacity-switch"
          />
          <p className="m-0 text-dense text-paper-low">{t('codex_capacity.switch.toggle_hint')}</p>
        </div>
      </div>

      <CodexCapacitySwitchDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        enabling={pending === true}
        loading={saving}
        reasonField={
          <FormField
            label={t('codex_capacity.switch.reason_label')}
            fieldId="codex-capacity-switch-reason"
            hint={t('codex_capacity.switch.reason_hint')}
          >
            <Input
              id="codex-capacity-switch-reason"
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
              data-testid="codex-capacity-switch-reason"
            />
          </FormField>
        }
        onConfirm={() => {
          if (pending === null) return;
          onSubmit({ enabled: pending, reason: reason.trim().length > 0 ? reason.trim() : null });
          setPending(null);
        }}
      />
    </Panel>
  );
}

function StateBanner({
  state,
  settings,
}: {
  readonly state: ReturnType<typeof readSwitchState>;
  readonly settings: CodexCapacityAlertSettings;
}): JSX.Element {
  const t = useT();
  const opsUserLabel = useOpsUserLabel();
  const tone =
    state === 'open'
      ? 'border-ok-line bg-ok-dim/50 text-ok'
      : state === 'closed'
        ? 'border-bad-line bg-bad-dim/50 text-bad-bright'
        : 'border-warn-line bg-warn-dim/50 text-warn';

  return (
    <div className={`flex flex-col gap-1.5 rounded border px-3 py-2.5 ${tone}`} data-testid="codex-capacity-switch-banner">
      <p className="m-0 text-body font-medium" data-testid="codex-capacity-switch-headline">
        {t(
          state === 'open'
            ? 'codex_capacity.switch.headline_open'
            : state === 'closed'
              ? 'codex_capacity.switch.headline_closed'
              : 'codex_capacity.switch.headline_never_opened',
        )}
      </p>
      <p className="m-0 text-dense text-paper-mid" data-testid="codex-capacity-switch-attribution">
        {settings.updated_at === null
          ? t('codex_capacity.switch.attribution_never')
          : t('codex_capacity.switch.attribution', {
              who: opsUserLabel(settings.updated_by_ops_user_id) || t('codex_capacity.switch.actor_unknown'),
              when: formatLocalTimestamp(settings.updated_at),
            })}
      </p>
      {settings.reason !== null && settings.reason.length > 0 ? (
        <p className="m-0 text-dense text-paper-mid" data-testid="codex-capacity-switch-reason-shown">
          {t('codex_capacity.switch.reason_shown', { reason: settings.reason })}
        </p>
      ) : null}
    </div>
  );
}
